import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import { FeeScheduleCategoryMaskedCurrency, MaskedFeeSchedule, MaskedTeam } from "work/masked-fields";
import VendorTeamSchema from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import { z } from "zod";

/** `ProposalResponse.HiredTerms`, as the API sends it: already masked for this reader. */
export const ProposalHiredTermsJSONZ = z.object({
  hiredAt: z.iso.datetime({ offset: true }),
  vendorTeam: z
    .intersection(VendorTeamSchema.VendorTeamJSONZ.optional(), z.object({ isMasked: z.boolean() }))
    .nullish(),
  feeSchedule: z.object({
    isMasked: z.boolean(),
    categories: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ).nullish(),
  }),
});

export type ProposalHiredTermsJSON = z.infer<typeof ProposalHiredTermsJSONZ>;

/**
 * The vendor team and fee schedule a proposal was hired on (#6633).
 *
 * A hired proposal's agreement is the work, and the work changes in place after the hire: a leader
 * hands over and leaves, a rate is replaced. These do not change. The server keeps them from the
 * hire and masks them for each reader exactly as it masks the live team and fees.
 */
export default class ProposalHiredTerms {
  public static fromJSON(json: unknown): ProposalHiredTerms | undefined {
    if (json === null || json === undefined) return undefined;
    const data = parseOrThrow(ProposalHiredTermsJSONZ, json, {
      logError: true,
      schemaName: "ProposalHiredTerms",
      operation: "fromJSON",
      payloadSample: json,
    });
    const hiredAt = AHDate.fromJSON(data.hiredAt);
    if (!hiredAt) return undefined;
    return new ProposalHiredTerms(hiredAt, vendorTeamOf(data.vendorTeam), feeScheduleOf(data.feeSchedule));
  }

  public readonly hiredAt: AHDate;
  public readonly vendorTeam: VendorTeam | MaskedTeam | null;
  public readonly feeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule;

  public constructor(
    hiredAt: AHDate,
    vendorTeam: VendorTeam | MaskedTeam | null,
    feeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule,
  ) {
    this.hiredAt = hiredAt;
    this.vendorTeam = vendorTeam;
    this.feeSchedule = feeSchedule;
  }
}

function vendorTeamOf(json: ProposalHiredTermsJSON["vendorTeam"]): VendorTeam | MaskedTeam | null {
  if (!json) return null;
  if (json.isMasked) return new MaskedTeam();
  return VendorTeam.fromJSON(json) ?? null;
}

function feeScheduleOf(
  json: ProposalHiredTermsJSON["feeSchedule"],
): FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule {
  if (json.isMasked) return new MaskedFeeSchedule();
  return (FeeScheduleCategory.fromJSONArray(json.categories ?? []) ?? []) as
    | FeeScheduleCategory[]
    | FeeScheduleCategoryMaskedCurrency[];
}
